import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaStar, FaCheck, FaTrash, FaClock, FaUser, FaBuilding } from 'react-icons/fa';
import PageHeader from './PageHeader';
import './ReviewManager.scss';

interface Review {
  id: number;
  name: string;
  company: string;
  position: string;
  rating: number;
  review: string;
  image?: string;
  approved: boolean;
  createdAt: string;
  updatedAt: string;
}

const ReviewManager: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const API_URL = 'http://localhost:4000/api/reviews';

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      
      let url = `${API_URL}/admin/all`;
      if (filter === 'pending') {
        url = `${API_URL}/admin/pending`;
      } else if (filter === 'approved') {
        url = `${API_URL}/approved`;
      }

      const response = await axios.get(url, {
        headers: filter !== 'approved' ? {
          Authorization: `Bearer ${token}`,
        } : {},
      });

      setReviews(response.data);
    } catch (err) {
      setError('Failed to fetch reviews');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const approveReview = async (id: number) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API_URL}/admin/${id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSuccessMessage('Review approved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReviews();
    } catch (err) {
      setError('Failed to approve review');
      console.error(err);
    }
  };

  const deleteReview = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSuccessMessage('Review deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchReviews();
    } catch (err) {
      setError('Failed to delete review');
      console.error(err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata',
    }).format(date);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar
        key={index}
        className={`review-manager__star ${
          index < rating ? 'review-manager__star--filled' : ''
        }`}
      />
    ));
  };

  const stats = {
    total: reviews.length,
    pending: reviews.filter((r) => !r.approved).length,
    approved: reviews.filter((r) => r.approved).length,
  };

  return (
    <div className="review-manager">
      <PageHeader 
        title="Review Management" 
        subtitle="Manage client reviews and testimonials"
      />

      {/* Statistics Cards */}
      <div className="review-manager__stats">
        <div className="review-manager__stat-card">
          <div className="review-manager__stat-icon review-manager__stat-icon--total">
            <FaUser />
          </div>
          <div className="review-manager__stat-info">
            <h3>{stats.total}</h3>
            <p>Total Reviews</p>
          </div>
        </div>
        <div className="review-manager__stat-card">
          <div className="review-manager__stat-icon review-manager__stat-icon--pending">
            <FaClock />
          </div>
          <div className="review-manager__stat-info">
            <h3>{stats.pending}</h3>
            <p>Pending Approval</p>
          </div>
        </div>
        <div className="review-manager__stat-card">
          <div className="review-manager__stat-icon review-manager__stat-icon--approved">
            <FaCheck />
          </div>
          <div className="review-manager__stat-info">
            <h3>{stats.approved}</h3>
            <p>Approved Reviews</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="review-manager__filters">
        <button
          className={`review-manager__filter-btn ${
            filter === 'all' ? 'review-manager__filter-btn--active' : ''
          }`}
          onClick={() => setFilter('all')}
        >
          All Reviews ({stats.total})
        </button>
        <button
          className={`review-manager__filter-btn ${
            filter === 'pending' ? 'review-manager__filter-btn--active' : ''
          }`}
          onClick={() => setFilter('pending')}
        >
          Pending ({stats.pending})
        </button>
        <button
          className={`review-manager__filter-btn ${
            filter === 'approved' ? 'review-manager__filter-btn--active' : ''
          }`}
          onClick={() => setFilter('approved')}
        >
          Approved ({stats.approved})
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="review-manager__success">{successMessage}</div>
      )}
      {error && <div className="review-manager__error">{error}</div>}

      {/* Reviews List */}
      {loading ? (
        <div className="review-manager__loading">Loading reviews...</div>
      ) : reviews.length === 0 ? (
        <div className="review-manager__empty">
          <p>No reviews found</p>
        </div>
      ) : (
        <div className="review-manager__list">
          {reviews.map((review) => (
            <div
              key={review.id}
              className={`review-manager__card ${
                !review.approved ? 'review-manager__card--pending' : ''
              }`}
            >
              <div className="review-manager__card-header">
                <div className="review-manager__reviewer-info">
                  {review.image ? (
                    <img
                      src={review.image}
                      alt={review.name}
                      className="review-manager__avatar"
                    />
                  ) : (
                    <div className="review-manager__avatar review-manager__avatar--placeholder">
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="review-manager__reviewer-name">
                      {review.name}
                    </h3>
                    <p className="review-manager__reviewer-position">
                      <FaBuilding /> {review.position} at {review.company}
                    </p>
                  </div>
                </div>
                <div className="review-manager__card-meta">
                  <div className="review-manager__rating">{renderStars(review.rating)}</div>
                  <span className="review-manager__date">
                    {formatDate(review.createdAt)}
                  </span>
                  {!review.approved && (
                    <span className="review-manager__pending-badge">
                      <FaClock /> Pending
                    </span>
                  )}
                </div>
              </div>

              <div className="review-manager__card-body">
                <p className="review-manager__review-text">{review.review}</p>
              </div>

              <div className="review-manager__card-actions">
                {!review.approved && (
                  <button
                    className="review-manager__btn review-manager__btn--approve"
                    onClick={() => approveReview(review.id)}
                  >
                    <FaCheck /> Approve
                  </button>
                )}
                <button
                  className="review-manager__btn review-manager__btn--delete"
                  onClick={() => deleteReview(review.id)}
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewManager;

